import { useEffect, useMemo, useRef, type MutableRefObject, type PointerEvent as ReactPointerEvent } from 'react';

import { suspendShortcuts } from '../../lib/shortcuts/index.js';
import {
  boundsOf,
  boxSelect,
  clickSelect,
  pick,
  pivotOf,
  selectedUvIndices,
  transformUvs,
  type UvLayout,
  type UvRect,
  type Vec2,
} from '../../lib/uv-doc.js';
import { useUv } from '../../state/uv.js';
import { drawCache, drawUv, frameView, screenToUv, zoomAt } from './uv-draw.js';
import { modalTransform, typeKey, type ModalKind, type ModalState } from './uv-modal.js';

/** What the editor's commands ask of the viewport. */
export interface UvViewportApi {
  /** G / R / S. */
  startTransform: (kind: ModalKind) => void;
  /** UV point under the mouse (null when it is outside). */
  pointerUv: () => Vec2 | null;
  /** Home (everything) or View Selected. */
  frame: (selected: boolean) => void;
}

const DRAG_PX = 4;
const PICK_PX = 9;

type Press =
  | { kind: 'select'; start: Vec2; shift: boolean; ctrl: boolean }
  | { kind: 'pan'; start: Vec2; center: Vec2 };

/**
 * The UV canvas: draws the layout and handles the mouse: wheel zoom, middle
 * (or Alt+left) drag pans, click / Shift+click / drag-box selects (Ctrl+drag
 * deselects), dragging a selected element moves it, right-click opens the UV
 * menu. Modal transforms take the keyboard while they run.
 */
export function UvViewport({
  layout,
  image,
  imageSize,
  apiRef,
  onCommit,
  onContextMenu,
}: {
  layout: UvLayout | null;
  image: CanvasImageSource | null;
  imageSize: Vec2 | null;
  apiRef: MutableRefObject<UvViewportApi | null>;
  /** Finished transform: the new UVs (one undo step). */
  onCommit: (uvs: Vec2[]) => void;
  onContextMenu: (x: number, y: number) => void;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const mode = useUv((s) => s.mode);
  const selection = useUv((s) => s.selection);
  const view = useUv((s) => s.view);
  const size = useUv((s) => s.size);
  const overlays = useUv((s) => s.overlays);
  const cache = useMemo(() => (layout ? drawCache(layout) : null), [layout]);

  const preview = useRef<Vec2[] | null>(null);
  const box = useRef<{ a: Vec2; b: Vec2 } | null>(null);
  const modal = useRef<ModalState | null>(null);
  const press = useRef<Press | null>(null);
  const mouse = useRef<Vec2 | null>(null);
  const raf = useRef(0);
  const live = useRef({ cache, mode, selection, view, size, overlays, image, imageSize, layout, onCommit });
  live.current = { cache, mode, selection, view, size, overlays, image, imageSize, layout, onCommit };

  const draw = useRef(() => {
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      raf.current = 0;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      const l = live.current;
      const dpr = globalThis.devicePixelRatio || 1;
      const w = Math.max(1, Math.round(l.size[0] * dpr));
      const h = Math.max(1, Math.round(l.size[1] * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const m = modal.current;
      drawUv(ctx, {
        size: l.size,
        view: l.view,
        cache: l.cache,
        uvs: preview.current,
        mode: l.mode,
        selection: l.selection,
        overlays: l.overlays,
        image: l.image,
        imageSize: l.imageSize,
        box: box.current,
        constraint: m?.axis ? { axis: m.axis, pivot: m.pivot } : null,
      });
    });
  }).current;

  useEffect(() => draw());
  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  // Track the canvas size.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      useUv.getState().setSize([Math.max(1, Math.round(r.width)), Math.max(1, Math.round(r.height))]);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  function frame(selected: boolean): void {
    const l = live.current;
    const st = useUv.getState();
    let min: Vec2 = [0, 0];
    let max: Vec2 = [1, 1];
    const uvs = l.layout?.uvs ?? [];
    const indices = selected ? selectedUvIndices(st.selection) : [];
    const bounds = selected && indices.length ? boundsOf(uvs, indices) : boundsOf(uvs, uvs.map((_, i) => i));
    if (bounds && selected && indices.length) {
      min = bounds.min;
      max = bounds.max;
    } else if (bounds) {
      min = [Math.min(0, bounds.min[0]), Math.min(0, bounds.min[1])];
      max = [Math.max(1, bounds.max[0]), Math.max(1, bounds.max[1])];
    }
    st.setView(frameView(min, max, st.size));
  }

  // Frame the tile and the part's UVs when another part is shown.
  const framedPath = useRef<string | null>(null);
  useEffect(() => {
    const path = layout?.path ?? null;
    const r = wrapRef.current?.getBoundingClientRect();
    if (!path || framedPath.current === path || !r || r.width < 2 || r.height < 2) return;
    framedPath.current = path;
    useUv.getState().setSize([Math.round(r.width), Math.round(r.height)]);
    frame(false);
  });

  function local(e: { clientX: number; clientY: number }): Vec2 {
    const r = wrapRef.current?.getBoundingClientRect();
    return r ? [e.clientX - r.left, e.clientY - r.top] : [0, 0];
  }

  function startTransform(kind: ModalKind, tweakFrom?: Vec2): void {
    const l = live.current;
    if (!l.layout?.editable || modal.current) return;
    const indices = selectedUvIndices(useUv.getState().selection);
    if (!indices.length) {
      useUv.getState().setStatus('Nothing selected to transform.');
      return;
    }
    const start = tweakFrom ?? mouse.current ?? [l.size[0] / 2, l.size[1] / 2];
    const base = l.layout.uvs;
    const state: ModalState = { kind, axis: null, start, pivot: pivotOf(base, indices), typed: '', tweak: Boolean(tweakFrom), indices, base };
    modal.current = state;
    const resume = suspendShortcuts();
    let ctrl = false;
    let last: Vec2 = mouse.current ?? start;

    const update = () => {
      const m = modal.current;
      if (!m) return;
      const v = live.current;
      const { transform, text } = modalTransform(m, last, v.view, v.size, ctrl);
      preview.current = transformUvs(m.base, m.indices, transform);
      useUv.getState().setModal({ kind: m.kind, axis: m.axis, text });
      draw();
    };
    const finish = (confirm: boolean) => {
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('pointerup', onUp, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('contextmenu', onMenu, true);
      window.removeEventListener('blur', onBlur);
      resume();
      const result = preview.current;
      modal.current = null;
      preview.current = null;
      useUv.getState().setModal(null);
      if (confirm && result) live.current.onCommit(result);
      draw();
    };
    const onMove = (e: PointerEvent) => {
      last = local(e);
      mouse.current = last;
      ctrl = e.ctrlKey || e.metaKey;
      update();
    };
    const onDown = (e: PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.button === 0) finish(true);
      else if (e.button === 2) finish(false);
    };
    const onUp = (e: PointerEvent) => {
      if (state.tweak && e.button === 0) {
        e.stopPropagation();
        finish(true);
      }
    };
    const onMenu = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onBlur = () => finish(false);
    const onKey = (e: KeyboardEvent) => {
      const m = modal.current;
      if (!m) return;
      const consume = () => {
        e.preventDefault();
        e.stopImmediatePropagation();
      };
      if (e.key === 'Control' || e.key === 'Meta') {
        ctrl = true;
        update();
        return;
      }
      if (e.key === 'Escape') {
        consume();
        finish(false);
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        consume();
        finish(true);
        return;
      }
      const k = e.key.toLowerCase();
      if (k === 'x' || k === 'y') {
        consume();
        const axis = k === 'x' ? 'u' : 'v';
        m.axis = m.axis === axis ? null : axis;
        update();
        return;
      }
      if (k === 'g' || k === 'r' || k === 's') {
        consume();
        const kind: ModalKind = k === 'g' ? 'grab' : k === 'r' ? 'rotate' : 'scale';
        if (kind !== m.kind) Object.assign(m, { kind, typed: '', axis: null });
        update();
        return;
      }
      const typed = typeKey(m.typed, e.key);
      if (typed !== null) {
        consume();
        m.typed = typed;
        update();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        ctrl = false;
        update();
      }
    };
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('contextmenu', onMenu, true);
    window.addEventListener('blur', onBlur);
    update();
  }

  apiRef.current = {
    startTransform: (kind) => startTransform(kind),
    pointerUv: () => (mouse.current ? screenToUv(live.current.view, live.current.size, mouse.current) : null),
    frame,
  };

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (modal.current) return;
    const s = local(e);
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      press.current = { kind: 'pan', start: s, center: useUv.getState().view.center };
      return;
    }
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    press.current = { kind: 'select', start: s, shift: e.shiftKey, ctrl: e.ctrlKey || e.metaKey };
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const s = local(e);
    mouse.current = s;
    const p = press.current;
    if (!p || modal.current) return;
    const st = useUv.getState();
    if (p.kind === 'pan') {
      const zoom = st.view.zoom;
      st.setView({ center: [p.center[0] - (s[0] - p.start[0]) / zoom, p.center[1] + (s[1] - p.start[1]) / zoom], zoom });
      return;
    }
    if (!box.current && Math.hypot(s[0] - p.start[0], s[1] - p.start[1]) < DRAG_PX) return;
    const l = live.current;
    if (!box.current && l.layout && l.layout.editable && !p.shift && !p.ctrl) {
      // Drag on an element: select it (unless it already is) and move the selection.
      const hit = pick(l.layout, screenToUv(st.view, st.size, p.start), st.mode, PICK_PX / st.view.zoom);
      if (hit) {
        const picked = clickSelect(l.layout, st.mode, st.selection, hit, false);
        const already = [...picked.uvs].every((i) => st.selection.uvs.has(i));
        if (!already) st.setSelection(l.layout.path, picked);
        press.current = null;
        // Selection state is read from the store when the transform starts.
        startTransform('grab', p.start);
        return;
      }
    }
    box.current = { a: p.start, b: s };
    draw();
  }

  function onPointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    const p = press.current;
    press.current = null;
    if (!p || p.kind !== 'select') return;
    const l = live.current;
    const st = useUv.getState();
    if (!l.layout) {
      box.current = null;
      draw();
      return;
    }
    const s = local(e);
    if (box.current) {
      const a = screenToUv(st.view, st.size, box.current.a);
      const b = screenToUv(st.view, st.size, s);
      const rect: UvRect = { min: [Math.min(a[0], b[0]), Math.min(a[1], b[1])], max: [Math.max(a[0], b[0]), Math.max(a[1], b[1])] };
      box.current = null;
      st.setSelection(l.layout.path, boxSelect(l.layout, st.mode, st.selection, rect, p.ctrl ? 'sub' : p.shift ? 'add' : 'set'));
      return;
    }
    const hit = pick(l.layout, screenToUv(st.view, st.size, s), st.mode, PICK_PX / st.view.zoom);
    st.setSelection(l.layout.path, clickSelect(l.layout, st.mode, st.selection, hit, p.shift));
  }

  return (
    <div
      ref={wrapRef}
      className="uv-canvas"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        press.current = null;
        box.current = null;
        draw();
      }}
      onPointerLeave={() => {
        if (!press.current) mouse.current = null;
      }}
      onWheel={(e) => {
        const st = useUv.getState();
        st.setView(zoomAt(st.view, st.size, local(e), Math.exp(-e.deltaY * 0.0015)));
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        if (!modal.current) onContextMenu(e.clientX, e.clientY);
      }}
      onAuxClick={(e) => e.preventDefault()}
    >
      <canvas ref={canvasRef} aria-label="UV layout" />
    </div>
  );
}
